import React, { useState } from 'react';
import { media } from '../../server';
import { useStateContext } from '../../context/ContextProvider';
import { renderText } from '../hooks/TextVeiw';
import { motion } from 'framer-motion';
import { FiStar, FiStar as FiStarOutline, FiHeart, FiShare2, FiCalendar } from 'react-icons/fi';
import { format } from 'date-fns';
import { FaQuoteLeft } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

function ReviewsCard({ reviews, isActive }) {
  const { currentDir } = useStateContext();
  const [isLiked, setIsLiked] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const {t}=useTranslation()
  // Calculate rating (random between 4.5 and 5 for demo)
  const rating = 5;
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;
  
  // Format date
  const formattedDate = reviews?.date 
    ? format(new Date(reviews.date), 'dd MMM yyyy')
    : 'Recently';

  // Truncate text based on expansion state
  const text = currentDir === "ltr" ? reviews?.textEn : reviews?.textAr;
  const truncatedText = text?.length > 120 && !isExpanded 
    ? text.substring(0, 120) + '...' 
    : text;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ 
        opacity: 1, 
        scale: 1,
        transition: { type: "spring", stiffness: 300, damping: 20 }
      }}
      whileHover={{ y: -8 }}
      className="relative h-full"
    >
      {/* Quote Icon Background */}
      <div className="absolute top-6 right-6 text-color-1/10 dark:text-white/5 z-0">
        <FaQuoteLeft size={48} />
      </div>

      {/* Main Card */}
      <div className="relative z-10 bg-white dark:bg-n-8 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-n-7 h-full flex flex-col transition-all duration-300 hover:shadow-2xl">
        
        {/* Rating Stars */}
        <div className="px-6 pt-6 pb-2">
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <FiStar
                key={i}
                className={`${
                  i < fullStars 
                    ? 'text-yellow-400 fill-yellow-400' 
                    : 'text-gray-300 dark:text-n-6'
                }`}
                size={18}
              />
            ))}
            <span className="ml-2 text-sm text-n-5 dark:text-n-4">5.0</span>
          </div>
        </div>

        {/* User Info */}
        <div className="px-6 py-3 flex items-center gap-4">
          <div className="relative">
            <div className="w-14 h-14 rounded-full bg-gradient-to-r from-color-1 to-color-2 p-0.5">
              <img
                src={`${media}${reviews?.Image}`}
                className="w-full h-full object-cover rounded-full"
                alt={reviews?.titleEn}
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white dark:border-n-8" />
          </div>
          <div className="flex-1">
            <h4 className="font-kufi font-bold text-lg text-n-7 dark:text-n-2">
              {currentDir === "ltr" ? reviews?.titleEn : reviews?.titleAr}
            </h4>
            <div className="flex items-center gap-2 text-xs text-n-5 dark:text-n-4">
              <FiCalendar size={12} />
              <span>{formattedDate}</span>
            </div>
          </div>
        </div>

        {/* Review Text */}
        <div className="px-6 py-3 flex-1">
          <div className="relative">
            <p 
              className={`text-n-6 font-kufi font-light dark:text-n-3 leading-relaxed text-sm ${
                !isExpanded && text?.length > 120 ? 'line-clamp-3' : ''
              }`}
            >
              {truncatedText}
            </p>
            {text?.length > 120 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-color-1 text-sm font-kufi  font-normal mt-2 hover:underline"
              >
                {isExpanded ? t("Show less") : t("Read more")}
              </button>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-n-7 flex items-center justify-between">
          <button
            onClick={() => setIsLiked(!isLiked)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-300 ${
              isLiked 
                ? 'text-red-500 bg-red-50 dark:bg-red-900/20' 
                : 'text-n-5 dark:text-n-4 hover:bg-gray-100 dark:hover:bg-n-7'
            }`}
          >
            <FiHeart className={isLiked ? 'fill-red-500' : ''} size={16} />
            <span className="text-sm font-kufi font-light">{isLiked ? t("Liked") : t("Like")}</span>
          </button>
          
          <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-n-5 dark:text-n-4 hover:bg-gray-100 dark:hover:bg-n-7 transition-all duration-300">
            <FiShare2 size={16} />
            <span className="text-sm font-kufi font-light">{t("Share")}</span>
          </button>
        </div>

        {/* Verified Badge */}
        <div className="absolute top-6 left-6">
          <div className="flex items-center gap-1 px-2 py-1 bg-green-500/10 text-green-600 rounded-full text-xs">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
            <span className='font-kufi font-light'>{t("Verified Purchase")}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default ReviewsCard;